/**
 * farm.ts — Categoría "Farmacia / Salud" (10 productos demo).
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_farm",
  skuPrefix: "FARM",
  categoria: "Farmacia / Salud",
} as const;

export const cat_farm_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Acetaminofén 500mg x20",
    marca: "MK",
    modelo: "Acetaminofén 500mg",
    origen: "Colombia",
    costo: 3200,
    precio: 5500,
    stock: 150,
    stockMinimo: 30,
    stockMaximo: 280,
    unidad: "caja",
    ubicacionFisica: "Estante F1, analgésicos",
    proveedor: "Tecnoquímicas S.A.",
    vencimientoMeses: 24,
    pesoNeto: "30g",
    descripcionLarga:
      "Acetaminofén 500mg, analgésico y antipirético. Alivia dolor leve a moderado y reduce la fiebre. Caja 20 tabletas.",
    especificaciones: [
      { clave: "Principio activo", valor: "Acetaminofén" },
      { clave: "Presentación", valor: "20 tabletas" },
    ],
    imageKeywords: ["paracetamol", "acetaminophen", "pills"],
    color: "bg-white",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Ibuprofeno 400mg x12",
    marca: "Genfar",
    origen: "Colombia",
    costo: 4500,
    precio: 7500,
    stock: 130,
    stockMinimo: 28,
    stockMaximo: 240,
    unidad: "caja",
    ubicacionFisica: "Estante F1, analgésicos",
    proveedor: "Laboratorios Genfar",
    vencimientoMeses: 24,
    descripcionLarga:
      "Ibuprofeno 400mg, antiinflamatorio no esteroideo. Eficaz para dolor muscular, menstrual, dental y reducción de inflamación.",
    especificaciones: [
      { clave: "Presentación", valor: "12 tabletas" },
      { clave: "Dosis", valor: "Cada 8h" },
    ],
    imageKeywords: ["ibuprofen", "anti-inflammatory", "medicine"],
    color: "bg-red-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Alcohol Antiséptico 70% 500ml",
    marca: "Antiseptik",
    origen: "Colombia",
    costo: 5500,
    precio: 8900,
    stock: 120,
    stockMinimo: 25,
    stockMaximo: 220,
    unidad: "botella",
    ubicacionFisica: "Estante F2, primeros auxilios",
    proveedor: "Productos Antiseptik",
    vencimientoMeses: 36,
    pesoNeto: "500ml",
    descripcionLarga:
      "Alcohol antiséptico 70% para desinfección de piel, heridas menores y superficies. Evaporación rápida, sin residuo.",
    especificaciones: [
      { clave: "Concentración", valor: "70%" },
      { clave: "Volumen", valor: "500ml" },
    ],
    imageKeywords: ["alcohol", "antiseptic", "sanitizer"],
    color: "bg-cyan-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Curitas Band-Aid x30",
    marca: "Band-Aid",
    modelo: "Flexible Fabric",
    origen: "EE.UU.",
    costo: 4800,
    precio: 7500,
    stock: 110,
    stockMinimo: 25,
    stockMaximo: 200,
    unidad: "caja",
    ubicacionFisica: "Estante F2, primeros auxilios",
    proveedor: "Johnson & Johnson Colombia",
    vencimientoMeses: 48,
    descripcionLarga:
      "Curitas flexibles de tela con almohadilla antiadherente. Variedad de tamaños para heridas superficiales. Hipoalergénicas.",
    especificaciones: [
      { clave: "Unidades", valor: "30 curitas" },
      { clave: "Material", valor: "Tela flexible" },
    ],
    imageKeywords: ["bandages", "bandaid", "first-aid"],
    color: "bg-amber-200",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Vitamina C 1g x30",
    marca: "MK",
    modelo: "Vitamina C 1g",
    origen: "Colombia",
    costo: 12500,
    precio: 18900,
    stock: 85,
    stockMinimo: 18,
    stockMaximo: 160,
    unidad: "caja",
    ubicacionFisica: "Estante F3, suplementos",
    proveedor: "Tecnoquímicas S.A.",
    vencimientoMeses: 24,
    descripcionLarga:
      "Vitamina C 1g efervescente, sabor naranja. Refuerza el sistema inmune y actúa como antioxidante. Caja 30 tabletas.",
    especificaciones: [
      { clave: "Dosis", valor: "1g" },
      { clave: "Presentación", valor: "30 efervescentes" },
    ],
    imageKeywords: ["vitamin-c", "supplement", "vitamins"],
    color: "bg-orange-400",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Termómetro Digital",
    marca: "Omron",
    modelo: "MC-246",
    origen: "Japón",
    costo: 22500,
    precio: 34900,
    stock: 40,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "unidad",
    ubicacionFisica: "Vitrina F4, dispositivos",
    proveedor: "Omron Healthcare Colombia",
    tiempoEntrega: "3 días",
    garantia: "1 año",
    descripcionLarga:
      "Termómetro digital axilar con lectura en 10 segundos. Pantalla LCD, memoria última lectura, alarma fiebre, IPX2.",
    especificaciones: [
      { clave: "Tiempo medición", valor: "10 seg" },
      { clave: "Precisión", valor: "±0.1°C" },
      { clave: "Batería", valor: "Incluida" },
    ],
    imageKeywords: ["thermometer", "digital-thermometer", "medical-device"],
    color: "bg-slate-300",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Tensiómetro Digital Brazo",
    marca: "Omron",
    modelo: "HEM-7120",
    origen: "Japón",
    costo: 125000,
    precio: 189000,
    stock: 20,
    stockMinimo: 5,
    stockMaximo: 40,
    unidad: "unidad",
    ubicacionFisica: "Vitrina F4, dispositivos",
    proveedor: "Omron Healthcare Colombia",
    tiempoEntrega: "3 días",
    garantia: "5 años",
    descripcionLarga:
      "Tensiómetro automático de brazo con detección de latidos irregulares. Memoria para 30 mediciones, brazalete 22-32cm.",
    especificaciones: [
      { clave: "Memoria", valor: "30 mediciones" },
      { clave: "Brazalete", valor: "22-32cm" },
    ],
    imageKeywords: [
      "blood-pressure-monitor",
      "tensiometro",
      "medical-equipment",
    ],
    color: "bg-white",
    shape: "square",
    gastoMargen: "estandar",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Gasa Estéril 10x10cm x10",
    marca: "MK",
    origen: "Colombia",
    costo: 3500,
    precio: 5900,
    stock: 130,
    stockMinimo: 28,
    stockMaximo: 240,
    unidad: "paquete",
    ubicacionFisica: "Estante F2, primeros auxilios",
    proveedor: "Tecnoquímicas S.A.",
    vencimientoMeses: 60,
    descripcionLarga:
      "Gasa estéril 100% algodón, 10x10cm. Individualmente empacada, esterilizada. Para curaciones y heridas moderadas.",
    especificaciones: [
      { clave: "Tamaño", valor: "10x10cm" },
      { clave: "Unidades", valor: "10" },
    ],
    imageKeywords: ["gauze", "medical-gauze", "bandage"],
    color: "bg-white",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Multivitamínico Centrum x30",
    marca: "Centrum",
    modelo: "Men/Women",
    origen: "EE.UU.",
    costo: 38500,
    precio: 58900,
    stock: 55,
    stockMinimo: 12,
    stockMaximo: 110,
    unidad: "frasco",
    ubicacionFisica: "Estante F3, suplementos",
    proveedor: "Pfizer Colombia",
    vencimientoMeses: 24,
    descripcionLarga:
      "Multivitamínico completo con 24 nutrientes esenciales: vitaminas A, C, D, E, B complex + minerales. Apoyo integral a la salud.",
    especificaciones: [
      { clave: "Unidades", valor: "30 tabletas" },
      { clave: "Nutrientes", valor: "24" },
    ],
    imageKeywords: ["multivitamin", "supplement", "vitamin-pills"],
    color: "bg-green-500",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Jeringa 10ml Estéril",
    marca: "BD",
    modelo: "Plastipak Luer",
    origen: "México",
    costo: 1200,
    precio: 2000,
    stock: 250,
    stockMinimo: 50,
    stockMaximo: 500,
    unidad: "unidad",
    ubicacionFisica: "Estante F2, primeros auxilios",
    proveedor: "Becton Dickinson Colombia",
    vencimientoMeses: 60,
    descripcionLarga:
      'Jeringa desechable 10ml con aguja 21G x 1½". Estéril, empaque individual. De uso clúnico y veterinario.',
    especificaciones: [
      { clave: "Volumen", valor: "10ml" },
      { clave: "Aguja", valor: '21G x 1½"' },
    ],
    imageKeywords: ["syringe", "medical-syringe", "injection"],
    color: "bg-slate-200",
    shape: "square",
  }),
];
